import type { SettingsMenuOption } from '@taki/ui';

/**
 * Une entree du selecteur de projets : son id, « client · projet », et de quoi
 * la RANGER dans le choix d'une tache.
 */
export interface OptionDeProjet {
  id: string;
  label: string;
  /** La valeur du champ `etat` du projet (`enCours`...), absente sans etat. */
  etat?: string;
  /**
   * LA DATE DU PROJET, en millisecondes : son debut (`startAt`) quand il en a
   * un, sa creation sinon. Elle departage deux projets de meme etape.
   */
  date?: number;
}

/** Une tache d'un projet : son id, son titre, et le projet qu'elle sert. */
export interface OptionDeTache {
  id: string;
  /** Le titre AFFICHE : le libelle de l'option quand le titre est une liste. */
  label: string;
  projectId: string;
  /** La valeur stockee quand le titre est une liste (`cm`) : cherchee, jamais montree. */
  cle?: string;
}

/** Seule une tache peut recevoir du temps. */
const TACHE = 't:';

/** L'etat d'un projet dont les taches viennent en tete (champ `etat` du modele). */
export const ETAT_EN_COURS = 'enCours';
/** Le titre du groupe de tete, puis celui du reste. */
export const GROUPE_EN_COURS = 'En cours';
export const GROUPE_AUTRES = 'Autres prestations';

/**
 * L'ORDRE DES PROJETS dans le choix d'une tache (lot du 14/10/2026) : ceux EN
 * COURS d'abord, puis les autres PAR ETAPE (l'ordre des options du champ
 * `etat` ; sans etat, ou d'un etat inconnu, en dernier), et dans une meme
 * etape LE PLUS RECENT D'ABORD (la prestation du mois avant celle du mois
 * d'avant), puis par nom.
 */
export function comparerProjets(
  ordreDesEtats: readonly string[],
): (a: OptionDeProjet, b: OptionDeProjet) => number {
  const rang = (projet: OptionDeProjet): number => {
    if (projet.etat === ETAT_EN_COURS) return -1;
    const index = projet.etat === undefined ? -1 : ordreDesEtats.indexOf(projet.etat);
    return index === -1 ? ordreDesEtats.length : index;
  };
  return (a, b) =>
    rang(a) - rang(b) ||
    (b.date ?? 0) - (a.date ?? 0) ||
    a.label.localeCompare(b.label, 'fr-FR');
}

/**
 * Chaque tache porte le nom de son projet en chemin devant elle (« Projet ›
 * Tache », `prefixe` de l'OptionList, que la recherche lit aussi). Une tache
 * dont le projet n'est pas au catalogue ne se propose pas : elle ne remplirait
 * que la moitie du couple.
 *
 * TOUT RESTE PROPOSE, mais range (`comparerProjets`). Quand au moins une tache
 * d'un projet en cours se propose, les entrees portent leur groupe, « En
 * cours » puis « Autres prestations » ; sans projet en cours, pas de titre :
 * un seul paquet n'a pas besoin de nom.
 *
 * LA RECHERCHE CROISE LES MOTS (`filtrerOptions` de `@taki/ui`) : « Moselle
 * CM » cherche « moselle » et « cm » dans le client, le projet et la tache,
 * clef stockee comprise (`motsCles`).
 */
export function entreesProjetTache(
  projets: readonly OptionDeProjet[],
  taches: readonly OptionDeTache[],
  ordreDesEtats: readonly string[] = [],
): SettingsMenuOption[] {
  const entrees = [...projets].sort(comparerProjets(ordreDesEtats)).flatMap((projet) =>
    taches
      .filter((tache) => tache.projectId === projet.id)
      .map((tache) => ({
        entree: {
          value: `${TACHE}${tache.id}`,
          label: tache.label,
          prefixe: projet.label,
          ...(tache.cle !== undefined && tache.cle !== tache.label ? { motsCles: tache.cle } : {}),
        },
        enCours: projet.etat === ETAT_EN_COURS,
      })),
  );
  const groupes = entrees.some(({ enCours }) => enCours);
  return entrees.map(({ entree, enCours }) =>
    groupes ? { ...entree, groupe: enCours ? GROUPE_EN_COURS : GROUPE_AUTRES } : entree,
  );
}

/** Le projet est toujours deduit de la tache, jamais choisi independamment. */
export function coupleDuChoix(
  valeur: string,
  taches: readonly OptionDeTache[],
): { projectId: string; taskId: string } {
  if (valeur.startsWith(TACHE)) {
    const tache = taches.find((candidate) => candidate.id === valeur.slice(TACHE.length));
    return tache === undefined ? { projectId: '', taskId: '' } : { projectId: tache.projectId, taskId: tache.id };
  }
  return { projectId: '', taskId: '' };
}

/** Le choix du selecteur pour une tache deja posee, vide sans tache. */
export function choixDuCouple(taskId: string): string {
  return taskId === '' ? '' : `${TACHE}${taskId}`;
}

/**
 * LE COUPLE A ENREGISTRER, ou `null` quand il manque la tache.
 *
 * LA TACHE N'EST OBLIGATOIRE QU'A LA CREATION (decision du fondateur du
 * 14/10/2026). Un ancien creneau sans tache, ou dont la tache a disparu du
 * catalogue, reste modifiable : sans nouveau choix, il garde EXACTEMENT son
 * projet et sa tache, il ne les perd pas parce que le selecteur est vide.
 */
export function coupleAEnregistrer(
  valeur: string,
  taches: readonly OptionDeTache[],
  existant: { projectId?: string | null; taskId?: string | null } | null,
): { projectId: string; taskId: string } | null {
  const couple = coupleDuChoix(valeur, taches);
  if (couple.taskId !== '') return couple;
  if (existant === null) return null;
  return { projectId: existant.projectId ?? '', taskId: existant.taskId ?? '' };
}
